import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { MarkDebtPaidRequest } from "@/api/types";
import { queryKeys } from "./queryKeys";
import { useConflictToast } from "./useConflictToast";

export interface QueryOptions {
  /** Gate the fetch (e.g. lazy-load the inactive settle-up view). Default true. */
  enabled?: boolean;
}

export function useSettlements(tripId: string | undefined, options: QueryOptions = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: tripId ? queryKeys.settlements(tripId) : ["settlements", "none"],
    queryFn: () => api.getSuggestedSettlements(tripId!),
    enabled: !!tripId && enabled,
  });
}

export function useMarkDebtPaid(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (input: {
      fromParticipantId: string;
      toParticipantId: string;
      body: MarkDebtPaidRequest;
    }) =>
      api.markDebtPaid(
        tripId,
        input.fromParticipantId,
        input.toParticipantId,
        input.body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
    },
    onError: (err) =>
      onConflict(err, [queryKeys.settlements(tripId)]),
  });
}

export function useUnmarkDebtPaid(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (input: { fromParticipantId: string; toParticipantId: string }) =>
      api.unmarkDebtPaid(tripId, input.fromParticipantId, input.toParticipantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
    },
    onError: (err) => onConflict(err, [queryKeys.settlements(tripId)]),
  });
}
